import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { validateLocation } from '@/lib/validation';

const AutocompleteInput = ({ onLocationSelect, theme, placeholder = "Where are you?", initialValue = "" }) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // Update input value when initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuggestions = async (input) => {
    if (input.length < 2) return;
    setIsLoading(true);
    
    try {
      console.log('🗺️ Racing geocoding APIs for instant suggestions...');
      
      // Create abort controllers for racing APIs
      const primaryController = new AbortController();
      const fallbackController = new AbortController();
      
      // Primary API call (Open-Meteo) - no racing for autocomplete, just timeout
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=10&language=en&format=json`,
        { 
          signal: AbortSignal.timeout(5000) // 5 second timeout
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.results || []);
      } else {
        // Fallback to common cities if API fails
        const commonCities = [
          { id: 1, name: 'Stockholm', latitude: 59.3293, longitude: 18.0686, country: 'Sweden', admin1: 'Stockholm' },
          { id: 2, name: 'New York', latitude: 40.7128, longitude: -74.0060, country: 'United States', admin1: 'New York' },
          { id: 3, name: 'London', latitude: 51.5074, longitude: -0.1278, country: 'United Kingdom', admin1: 'England' },
          { id: 4, name: 'Paris', latitude: 48.8566, longitude: 2.3522, country: 'France', admin1: 'Île-de-France' },
          { id: 5, name: 'Tokyo', latitude: 35.6762, longitude: 139.6503, country: 'Japan', admin1: 'Tokyo' }
        ];
        
        const filtered = commonCities.filter(city => 
          city.name.toLowerCase().includes(input.toLowerCase()) ||
          city.country.toLowerCase().includes(input.toLowerCase())
        );
        setSuggestions(filtered);
      }

    } catch (error) {
      console.error('Error fetching suggestions:', error);
      // Show common cities as fallback
      const commonCities = [
        { id: 1, name: 'Stockholm', latitude: 59.3293, longitude: 18.0686, country: 'Sweden', admin1: 'Stockholm' },
        { id: 2, name: 'New York', latitude: 40.7128, longitude: -74.0060, country: 'United States', admin1: 'New York' },
        { id: 3, name: 'London', latitude: 51.5074, longitude: -0.1278, country: 'United Kingdom', admin1: 'England' }
      ];
      
      const filtered = commonCities.filter(city => 
        city.name.toLowerCase().includes(input.toLowerCase())
      );
      setSuggestions(filtered);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    try {
      const value = e.target.value;
      // Validate and sanitize input
      const sanitizedValue = validateLocation(value);
      setInputValue(sanitizedValue);
      setShowSuggestions(true);
      fetchSuggestions(sanitizedValue);
    } catch (error) {
      // Allow typing but with basic sanitization on validation error
      const value = e.target.value.replace(/[<>\"'&]/g, '').substring(0, 100);
      setInputValue(value);
      setShowSuggestions(true);
      fetchSuggestions(value);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const locationName = suggestion.admin1 && suggestion.country 
      ? `${suggestion.name}, ${suggestion.admin1}, ${suggestion.country}`
      : suggestion.name;
    
    setInputValue(locationName);
    setShowSuggestions(false);
    onLocationSelect({
      name: locationName,
      location: locationName,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
      coordinates: {
        latitude: suggestion.latitude,
        longitude: suggestion.longitude
      }
    });
  };

  const formatLocationDetails = (suggestion) => {
    const details = [];
    if (suggestion.admin4) details.push(suggestion.admin4);
    if (suggestion.admin3) details.push(suggestion.admin3);
    if (suggestion.admin2) details.push(suggestion.admin2);
    if (suggestion.admin1) details.push(suggestion.admin1);
    if (suggestion.country) details.push(suggestion.country);
    
    return details.filter((detail, index, self) => 
      // Remove duplicates and empty strings
      detail && self.indexOf(detail) === index
    ).join(', ');
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`w-full border-gray-200 focus:border-gray-400 focus:ring-gray-400 text-sm ${
          theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'
        }`}
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul
          ref={suggestionsRef}
          className={`absolute z-10 w-full mt-1 border border-gray-200 rounded-md shadow-lg ${
            theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'
          }`}
        >
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <div className="text-sm">
                <span className="font-medium">{suggestion.name}</span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  {formatLocationDetails(suggestion)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;